import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { UserRole } from '../../../../shared/types';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { consumeReturnTo, peekReturnTo, rememberReturnTo } from '@/lib/return-to';

interface ProtectedRouteProps {
  /** Array of roles that are allowed. If empty/undefined, any authenticated user passes. */
  requiredRoles?: UserRole[];
  /** Extra permission(s) (e.g. 'tab:performance') that can substitute for a required role — any-of. */
  requiredExtraPermission?: string | string[];
  /** Sub-tab ID (e.g. 'subtab:performance:history') — blocks if explicitly denied. */
  deniedIfSubTab?: string;
  authHook: () => {
    user: any;
    isLoading: boolean;
    isAuthenticated: boolean;
  };
}

export function ProtectedRoute({ requiredRoles, requiredExtraPermission, deniedIfSubTab, authHook }: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = authHook();
  const location = useLocation();
  const navigate = useNavigate();
  const here = `${location.pathname}${location.search}${location.hash}`;

  // De vuelta del login (Cognito siempre vuelve a `/` → /dashboard): ir a la
  // ruta que se quería abrir. Se consume en un effect (una sola vez, también
  // con el doble montaje de StrictMode) y mientras tanto se muestra el loader
  // para no pintar la home un instante.
  const pendingReturn = isAuthenticated ? peekReturnTo() : null;
  const mustReturn = !!pendingReturn && pendingReturn !== here;
  useEffect(() => {
    if (!isAuthenticated || !pendingReturn) return;
    const path = consumeReturnTo();
    if (path && path !== here) navigate(path, { replace: true });
  }, [isAuthenticated, pendingReturn, here, navigate]);

  if (isLoading || mustReturn) {
    return (
      <div className="flex h-screen items-center justify-center">
        <WinclapLoader size={80} />
      </div>
    );
  }

  if (!isAuthenticated) {
    rememberReturnTo(location);
    return <Navigate to="/login" replace />;
  }

  const deniedPerms: string[] = user?.deniedPermissions ?? [];

  // Sub-tab denial check — block if this specific sub-tab is explicitly denied
  if (deniedIfSubTab && deniedPerms.includes(deniedIfSubTab)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles: UserRole[] = user?.roles ?? (user?.role ? [user.role] : []);
    const extraPerms: string[] = user?.extraPermissions ?? [];

    const extraPermList = requiredExtraPermission
      ? Array.isArray(requiredExtraPermission) ? requiredExtraPermission : [requiredExtraPermission]
      : [];

    const hasRoleAccess = requiredRoles.some((r) => userRoles.includes(r));
    const hasExtraPermAccess = extraPermList.some((p) => extraPerms.includes(p));

    // Explicit denial overrides role access (any denied perm of the list wins)
    const isDenied = extraPermList.some((p) => deniedPerms.includes(p));

    if ((!hasRoleAccess && !hasExtraPermAccess) || isDenied) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <Outlet />;
}
